import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ImportPreview, ImportResult } from '@messe/shared'
import { Button } from './ui/button.js'
import { CheckboxField, SelectField } from './ui/field.js'
import { Alert } from './ui/feedback.js'
import { Dialog } from './ui/dialog.js'
import { format, t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { fileToBase64, previewImport, runImport } from '../lib/value-lists.js'

interface Props {
  open: boolean
  listId: string
  isSupplierList: boolean
  onClose: () => void
}

type Stage = 'file' | 'mapping' | 'result'

/** Column targets offered in the mapping step. */
const SUPPLIER_METADATA_TARGETS = [
  { key: 'supplierNumber', labelKey: 'supplierNumber' },
  { key: 'country', labelKey: 'country' },
  { key: 'contact', labelKey: 'contact' },
  { key: 'note', labelKey: 'note' },
] as const

export function CsvImportDialog({ open, listId, isSupplierList, onClose }: Props) {
  const strings = t()
  const queryClient = useQueryClient()

  const [stage, setStage] = useState<Stage>('file')
  const [fileName, setFileName] = useState('')
  const [contentBase64, setContentBase64] = useState('')
  const [hasHeaderRow, setHasHeaderRow] = useState(true)
  const [encoding, setEncoding] = useState<'auto' | 'utf-8' | 'utf-8-bom' | 'windows-1252'>('auto')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const [labelColumn, setLabelColumn] = useState<number | null>(null)
  const [valueColumn, setValueColumn] = useState<number | null>(null)
  const [sortColumn, setSortColumn] = useState<number | null>(null)
  const [metadataColumns, setMetadataColumns] = useState<Record<number, string>>({})
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'update'>('skip')

  useEffect(() => {
    if (!open) return
    setStage('file')
    setFileName('')
    setContentBase64('')
    setHasHeaderRow(true)
    setEncoding('auto')
    setPreview(null)
    setResult(null)
    setFormError(null)
    setLabelColumn(null)
    setValueColumn(null)
    setSortColumn(null)
    setMetadataColumns({})
    setOnDuplicate('skip')
  }, [open])

  const loadPreview = useMutation({
    mutationFn: async (input: { contentBase64: string; fileName: string }) =>
      previewImport(listId, {
        fileName: input.fileName,
        contentBase64: input.contentBase64,
        ...(encoding === 'auto' ? {} : { encoding }),
        hasHeaderRow,
      }),
    onSuccess: (data) => {
      setPreview(data)
      // Guess the obvious mapping: with a header row, a column called "Name"
      // is the label. Saves the common case a click without hiding the choice.
      const nameIndex = data.headers.findIndex((header) => /name|bezeichnung/i.test(header))
      setLabelColumn(nameIndex >= 0 ? nameIndex : 0)
      setStage('mapping')
    },
    onError: (error) => {
      setFormError(
        error instanceof ApiError
          ? (error.problem.detail ?? strings.errors.unexpected)
          : strings.errors.unexpected,
      )
    },
  })

  const doImport = useMutation({
    mutationFn: () => {
      if (labelColumn === null) throw new Error('No label column')
      return runImport(listId, {
        fileName,
        contentBase64,
        settings: {
          ...(encoding === 'auto' ? {} : { encoding }),
          ...(preview ? { delimiter: preview.detectedDelimiter } : {}),
          hasHeaderRow,
          mapping: {
            label: labelColumn,
            value: valueColumn,
            sortOrder: sortColumn,
            metadata: Object.fromEntries(Object.entries(metadataColumns)),
          },
          onDuplicate,
        },
      })
    },
    onSuccess: async (data) => {
      setResult(data)
      setStage('result')
      await queryClient.invalidateQueries({ queryKey: ['value-list-items'] })
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
    },
    onError: (error) => {
      setFormError(
        error instanceof ApiError
          ? (error.problem.detail ?? strings.errors.unexpected)
          : strings.errors.unexpected,
      )
    },
  })

  async function handleFile(file: File | undefined) {
    if (!file) return
    setFormError(null)
    setFileName(file.name)

    try {
      const encoded = await fileToBase64(file)
      setContentBase64(encoded)
      loadPreview.mutate({ contentBase64: encoded, fileName: file.name })
    } catch {
      setFormError(strings.errors.unexpected)
    }
  }

  const columnOptions = (preview?.headers ?? []).map((header, index) => ({
    value: String(index),
    label: `${String(index + 1)}: ${header}`,
  }))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={strings.valueLists.import.heading}
      footer={
        stage === 'result' ? (
          <Button onClick={onClose}>{strings.valueLists.import.close}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {strings.valueLists.actions.cancel}
            </Button>
            {stage === 'mapping' && (
              <Button
                disabled={doImport.isPending}
                onClick={() => {
                  setFormError(null)
                  if (labelColumn === null) {
                    setFormError(strings.valueLists.import.noLabelColumn)
                    return
                  }
                  doImport.mutate()
                }}
              >
                {doImport.isPending
                  ? strings.valueLists.import.importing
                  : strings.valueLists.import.submit}
              </Button>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        {stage === 'file' && (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="csv-file" className="text-sm font-medium">
                {strings.valueLists.import.file}
              </label>
              <input
                id="csv-file"
                type="file"
                accept=".csv,text/csv"
                className="text-sm"
                onChange={(event) => void handleFile(event.target.files?.[0])}
              />
            </div>

            <CheckboxField
              label={strings.valueLists.import.hasHeaderRow}
              checked={hasHeaderRow}
              onChange={(event) => setHasHeaderRow(event.target.checked)}
            />

            <SelectField
              label={strings.valueLists.import.encoding}
              hint={strings.valueLists.import.detectedHint}
              value={encoding}
              options={[
                { value: 'auto', label: strings.valueLists.import.encodingAuto },
                { value: 'utf-8', label: 'UTF-8' },
                { value: 'utf-8-bom', label: 'UTF-8 mit BOM' },
                { value: 'windows-1252', label: 'Windows-1252' },
              ]}
              onChange={(event) =>
                setEncoding(event.target.value as 'auto' | 'utf-8' | 'utf-8-bom' | 'windows-1252')
              }
            />

            {loadPreview.isPending && (
              <p className="text-text-muted text-sm">{strings.app.loading}</p>
            )}
          </>
        )}

        {stage === 'mapping' && preview && (
          <>
            <Alert tone="info">
              {format(strings.valueLists.import.detected, {
                encoding: preview.detectedEncoding,
                delimiter: preview.detectedDelimiter === '\t' ? 'Tab' : preview.detectedDelimiter,
              })}
            </Alert>

            <div>
              <h3 className="mb-2 text-sm font-medium">{strings.valueLists.import.preview}</h3>
              <div className="border-border max-h-40 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-surface-sunken text-text-muted">
                    <tr>
                      {preview.headers.map((header, index) => (
                        <th key={index} className="px-2 py-1 text-left font-medium">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sampleRows.slice(0, 5).map((row) => (
                      <tr key={row.line} className="border-border border-t">
                        {row.values.map((value, index) => (
                          <td key={index} className="px-2 py-1">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-text-muted mt-1 text-xs">
                {format(strings.valueLists.import.previewRows, { count: preview.totalRows })}
              </p>
            </div>

            <h3 className="text-sm font-medium">{strings.valueLists.import.mapping}</h3>

            <SelectField
              label={strings.valueLists.import.mapLabel}
              value={labelColumn === null ? '' : String(labelColumn)}
              options={columnOptions}
              onChange={(event) => setLabelColumn(Number.parseInt(event.target.value, 10))}
            />

            <SelectField
              label={strings.valueLists.import.mapValue}
              value={valueColumn === null ? '' : String(valueColumn)}
              options={[{ value: '', label: strings.valueLists.import.mapNone }, ...columnOptions]}
              onChange={(event) =>
                setValueColumn(
                  event.target.value === '' ? null : Number.parseInt(event.target.value, 10),
                )
              }
            />

            <SelectField
              label={strings.valueLists.import.mapSortOrder}
              value={sortColumn === null ? '' : String(sortColumn)}
              options={[{ value: '', label: strings.valueLists.import.mapNone }, ...columnOptions]}
              onChange={(event) =>
                setSortColumn(
                  event.target.value === '' ? null : Number.parseInt(event.target.value, 10),
                )
              }
            />

            {isSupplierList &&
              SUPPLIER_METADATA_TARGETS.map((target) => {
                const current = Object.entries(metadataColumns).find(
                  ([, key]) => key === target.key,
                )?.[0]

                return (
                  <SelectField
                    key={target.key}
                    label={strings.valueLists.columns[target.labelKey]}
                    value={current ?? ''}
                    options={[
                      { value: '', label: strings.valueLists.import.mapNone },
                      ...columnOptions,
                    ]}
                    onChange={(event) => {
                      setMetadataColumns((previous) => {
                        // Rebuilt rather than mutated: a target maps to exactly
                        // one column, so the old assignment has to go.
                        const next = Object.fromEntries(
                          Object.entries(previous).filter(([, key]) => key !== target.key),
                        )
                        if (event.target.value !== '') {
                          next[Number.parseInt(event.target.value, 10)] = target.key
                        }
                        return next
                      })
                    }}
                  />
                )
              })}

            <SelectField
              label={strings.valueLists.import.onDuplicate}
              value={onDuplicate}
              options={[
                { value: 'skip', label: strings.valueLists.import.onDuplicateSkip },
                { value: 'update', label: strings.valueLists.import.onDuplicateUpdate },
              ]}
              onChange={(event) => setOnDuplicate(event.target.value as 'skip' | 'update')}
            />
          </>
        )}

        {stage === 'result' && result && (
          <>
            <Alert tone={result.issues.length === 0 ? 'success' : 'warning'}>
              {format(strings.valueLists.import.resultSummary, {
                created: result.created,
                updated: result.updated,
                skipped: result.skipped,
              })}
            </Alert>

            {result.issues.length > 0 && (
              <div>
                <h3 className="text-danger mb-2 text-sm font-medium">
                  {format(strings.valueLists.import.issuesHeading, {
                    count: result.issues.length,
                  })}
                </h3>
                <ul className="border-border max-h-48 divide-y overflow-y-auto rounded-md border text-sm">
                  {result.issues.map((issue) => (
                    <li key={`${String(issue.line)}-${issue.message}`} className="px-3 py-2">
                      <span className="text-text-muted text-xs">
                        {format(strings.valueLists.import.issueLine, { line: issue.line })}
                      </span>
                      <p>{issue.message}</p>
                      <p className="text-text-muted truncate text-xs">{issue.values.join(' · ')}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}
